import { hashString, seededRandom } from "@/lib/hash";
import type { ModuleId } from "@/router/routes";

import { sampleDrawings } from "../samples";
import { startingDrawings } from "../starting-drawings";
import { disciplineOf } from "../taxonomy";
import type {
  ActivityItem,
  AttentionItem,
  DrawingSummary,
  Milestone,
  ProjectDetail,
  ProjectSummary,
} from "../types";

/** Generates believable, deterministic detail (milestones, attention, activity, drawings) for a sample project. */

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;
/** Days between consecutive stage milestones in generated schedules. */
const STAGE_SPACING_DAYS = 21;
const ACTIVITY_ITEMS = 9;
/** Activity is spread over this many minutes before the project's last update. */
const ACTIVITY_SPAN_MINS = 6 * 24 * 60;

const ATTENTION: readonly Omit<AttentionItem, "id" | "at">[] = [
  {
    kind: "mention",
    title: "Rohan mentioned you on Ground floor plan",
    module: "design",
    tab: "draft",
  },
  {
    kind: "approval",
    title: "Client approval waiting: plan options",
    module: "studio",
    tab: "client",
  },
  {
    kind: "task",
    title: "Task due today: update the area statement",
    module: "studio",
    tab: "tasks",
  },
  {
    kind: "mention",
    title: "Meera asked about the kitchen counter height",
    module: "design",
    tab: "draft",
  },
  { kind: "stalled", title: "Stage idle for 10 working days", module: "studio", tab: "overview" },
];

const ACTIVITY: readonly { module: ModuleId; text: string; ai?: boolean }[] = [
  { module: "design", text: "edited Ground floor plan" },
  { module: "design", text: "drafted a door schedule with AI", ai: true },
  { module: "site", text: "added 12 site photos" },
  { module: "design", text: "issued 8 sheets for client review" },
  { module: "render", text: "rendered “Street view, golden hour”" },
  { module: "studio", text: "marked “Concept options” approved by the client" },
  { module: "design", text: "added a layer for the structural grid" },
  { module: "studio", text: "assigned 3 tasks for the next stage" },
  { module: "site", text: "updated the plot boundary from the survey" },
  { module: "design", text: "checked the area statement with AI", ai: true },
];

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function milestones(project: ProjectSummary, now: number): Milestone[] {
  const stages = disciplineOf(project.discipline).stages;
  return stages.map((stage, index) => ({
    id: `${project.id}-m${String(index)}`,
    title: `${stage.name} sign-off`,
    due: iso(now + (index - project.stageIndex) * STAGE_SPACING_DAYS * MS_PER_DAY),
    stageIndex: index,
    done: index < project.stageIndex,
  }));
}

export function buildDetail(
  project: ProjectSummary,
  now: number,
  extra: { address?: string; plotAreaM2?: number },
): ProjectDetail {
  const random = seededRandom(hashString(project.id));
  const updated = new Date(project.updatedAt).getTime();
  // A project created in the wizard (the only mock project with no sample ID) starts with no activity and no files.
  const fresh = project.sampleId === undefined;
  const attentionItems = ATTENTION.slice(0, fresh ? 0 : project.attention).map((item, index) => ({
    ...item,
    id: `${project.id}-a${String(index)}`,
    at: iso(updated - Math.round(random() * ACTIVITY_SPAN_MINS) * MS_PER_MINUTE),
  }));
  const activity: ActivityItem[] = Array.from(
    { length: fresh ? 0 : ACTIVITY_ITEMS },
    (_, index) => {
      const template =
        ACTIVITY[(index + Math.floor(random() * ACTIVITY.length)) % ACTIVITY.length] ?? ACTIVITY[0];
      const personId = project.team[index % project.team.length] ?? project.team[0] ?? "";
      const at =
        updated -
        Math.round((index / ACTIVITY_ITEMS) * ACTIVITY_SPAN_MINS * (0.6 + random() * 0.4)) *
          MS_PER_MINUTE;
      return {
        id: `${project.id}-e${String(index)}`,
        actor:
          template?.ai === true
            ? { kind: "ai", requestId: `req-${String(index)}`, personId }
            : { kind: "person", personId },
        module: template?.module ?? "design",
        text: template?.text ?? "",
        at: iso(at),
      };
    },
  );
  // A sample project lists the sample's drawings (FR-PROJ-009), which the doc service makes from its own copies.
  const listed =
    !project.sample || project.sampleId === undefined
      ? startingDrawings(project.discipline)
      : sampleDrawings(project.sampleId);
  const drawings: DrawingSummary[] = listed.map(({ id, name, kind }, index) => ({
    id,
    name,
    kind,
    updatedAt: iso(updated - index * random() * MS_PER_DAY),
  }));
  return {
    ...project,
    ...extra,
    milestones: milestones(project, now),
    attentionItems,
    activity,
    drawings,
    fileCount: fresh ? 0 : Math.round(4 + random() * 30),
  };
}
